/* SherpaHolidays inquiry tracking.
 * Counts the configured Shopify Forms success redirect only after a recent
 * submission in the same tab. A submit event alone never sends a GA4 event.
 * No field values are read. No tag, consent setting, or page view is created.
 */
(() => {
  'use strict';

  const script = document.querySelector('[data-sherpa-inquiry-tracking]');
  if (!script || script.dataset.trackingInitialized) return;
  script.dataset.trackingInitialized = 'true';

  const config = script.dataset;
  const key = 'sherpa_inquiry_pending_v1';
  const maxAge = 5 * 60 * 1000;
  const normalize = (path) => path.replace(/\/+$/, '') || '/';
  const contactPath = normalize(config.contactPath);
  const formPaths = [contactPath];
  if (config.landingPath) formPaths.push(normalize(config.landingPath));
  const confirmationPath = normalize(config.confirmationPath);
  const path = normalize(window.location.pathname);
  const status = (value) => { script.dataset.trackingStatus = value; };

  function removePending() {
    try { window.sessionStorage.removeItem(key); } catch (_) { /* Fail closed. */ }
  }

  function consumePending() {
    try {
      const value = window.sessionStorage.getItem(key);
      window.sessionStorage.removeItem(key);
      return value ? JSON.parse(value) : null;
    } catch (_) {
      return null;
    }
  }

  if (formPaths.includes(path)) {
    removePending();
    status('waiting-for-form');
    const observed = new WeakSet();
    const handled = new WeakSet();
    const observers = [];
    let scanQueued = false;

    function rememberSubmission(event) {
      if (event.target.tagName !== 'FORM' || !event.isTrusted || handled.has(event)) return;
      handled.add(event);
      const pending = {
        formId: config.formId,
        submittedAt: Date.now(),
        contactPath: path,
        confirmationPath,
        debug: new URLSearchParams(window.location.search).get('sh_lead_debug') === '1'
      };
      try {
        window.sessionStorage.setItem(key, JSON.stringify(pending));
        status('submission-marked');
      } catch (_) {
        status('storage-unavailable');
      }
    }

    function invalidate() {
      removePending();
    }

    function observeTree(root) {
      if (!observed.has(root)) {
        observed.add(root);
        root.addEventListener('submit', rememberSubmission, true);
        root.addEventListener('invalid', invalidate, true);
        root.addEventListener('input', invalidate, true);
        const observer = new MutationObserver(queueScan);
        observer.observe(root, { childList: true, subtree: true });
        observers.push(observer);
      }
      if (root.querySelector('form')) status('form-ready');
      if (root.shadowRoot) observeTree(root.shadowRoot);
      root.querySelectorAll('*').forEach((element) => {
        if (element.shadowRoot) observeTree(element.shadowRoot);
      });
    }

    function scan() {
      scanQueued = false;
      document.querySelectorAll('[data-forms-id]').forEach((root) => {
        if (root.dataset.formsId === config.formRoot) observeTree(root);
      });
    }

    function queueScan() {
      if (!scanQueued) {
        scanQueued = true;
        queueMicrotask(scan);
      }
    }

    const discovery = new MutationObserver(queueScan);
    discovery.observe(document.body, { childList: true, subtree: true });
    // These composed events also discover shadow roots attached after mounting.
    document.addEventListener('pointerdown', scan, true);
    document.addEventListener('focusin', scan, true);
    // Browser back/forward restoration must not reuse an abandoned attempt.
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        removePending();
        scan();
      }
    });
    scan();
    return;
  }

  if (path !== confirmationPath) return;
  const pending = consumePending();
  if (!pending) {
    status('ignored-no-submission');
    return;
  }

  let referredFromContact = false;
  try {
    const referrer = new URL(document.referrer);
    referredFromContact = referrer.origin === window.location.origin &&
      normalize(referrer.pathname) === pending.contactPath;
  } catch (_) { /* Missing or external referrers do not qualify. */ }
  const navigation = window.performance.getEntriesByType('navigation')[0];
  const age = Date.now() - pending.submittedAt;
  if (!referredFromContact || !navigation || navigation.type !== 'navigate' ||
      pending.formId !== config.formId || !formPaths.includes(pending.contactPath) ||
      pending.confirmationPath !== confirmationPath || !Number.isFinite(age) ||
      age < 0 || age > maxAge) {
    status('ignored-invalid-redirect');
    return;
  }

  let finished = false;
  let privacyRequested = false;
  let timer;
  const deadline = Date.now() + 30000;

  function stop(value) {
    finished = true;
    window.clearTimeout(timer);
    document.removeEventListener('visitorConsentCollected', trySend);
    status(value);
  }

  function analyticsAllowed() {
    // Honor the existing theme banner's explicit decline as well as Shopify.
    if (document.cookie.split(';').some((part) =>
      part.trim() === 'hldn_consent_choice=declined')) return false;
    try {
      return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true;
    } catch (_) {
      return false;
    }
  }

  function existingTracker() {
    for (const element of document.scripts) {
      if (!element.src) continue;
      try {
        const url = new URL(element.src);
        if (url.hostname !== 'www.googletagmanager.com' || url.pathname !== '/gtag/js' ||
            url.searchParams.get('id') !== config.measurementId) continue;
        if (typeof window.gtag === 'function') return window.gtag;
        const layer = window[url.searchParams.get('l') || 'dataLayer'];
        if (Array.isArray(layer)) {
          return function () { layer.push(arguments); };
        }
      } catch (_) { /* An unrelated script does not affect the form. */ }
    }
    return null;
  }

  function trySend() {
    if (finished) return;
    window.clearTimeout(timer);
    if (Date.now() > deadline) {
      stop('not-sent-consent-or-tag-unavailable');
      return;
    }
    if (!window.Shopify?.customerPrivacy && !privacyRequested &&
        typeof window.Shopify?.loadFeatures === 'function') {
      privacyRequested = true;
      try {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }],
          () => { if (!finished) trySend(); });
      } catch (_) { /* Retry only the readiness check, never the submission. */ }
    }
    if (finished) return;
    const tracker = analyticsAllowed() ? existingTracker() : null;
    if (tracker) {
      const parameters = {
        send_to: config.measurementId,
        form_id: config.formId,
        form_name: 'trip_inquiry'
      };
      if (pending.debug === true) parameters.debug_mode = true;
      // Consume once before handing the event to the existing Google tag.
      stop('queued');
      try {
        tracker('event', 'generate_lead', parameters);
      } catch (_) {
        status('not-sent-tag-error');
      }
      return;
    }
    status('waiting-for-consent-or-tag');
    timer = window.setTimeout(trySend, 250);
  }

  document.addEventListener('visitorConsentCollected', trySend);
  trySend();
})();

